import { useState } from "react";

/**
 * The last non-empty value, kept for as long as the current one is empty.
 *
 * This is what makes an overlay's exit possible (ADR-0091). The common shape in
 * the app is `{row ? <ConfirmDialog open …>{row.title}</ConfirmDialog> : null}`:
 * the mount IS the open state, so the dialog is destroyed before it can animate
 * out. Handing the flag to `open` fixes the motion but breaks the body, because
 * on the closing frame `row` is already null and `row.title` throws.
 *
 * Retaining the value keeps the closing dialog rendering what it was showing,
 * which is also what a reader expects to see while it fades:
 *
 *   const shown = useRetainedValue(pendingUnlink);
 *   <ConfirmDialog open={pendingUnlink !== null} title={shown?.title ?? ""} />
 *
 * Held as state derived during render, not a ref: a prop change is a
 * state-machine event, and React retries the component with the new value
 * before committing, so nothing is painted from the stale one. This is the
 * same shape as `useTransitionPresence`.
 */
export function useRetainedValue<T>(value: T | null | undefined): T | null {
  const [retained, setRetained] = useState<T | null>(value ?? null);
  if (value !== null && value !== undefined && value !== retained) {
    setRetained(value);
  }
  return value ?? retained;
}
